import { trace, SpanStatusCode } from '@opentelemetry/api'
import { LambdaMetrics } from '@moonpig/common-monitoring-lambda'
import { logger } from '../logger'

type MetricDimensions = { [id: string]: string }
type TraceAnnotations = { [id: string]: string | number | boolean }
export type MetricsCountOptions = {
  metricName: string
  metricDimensions?: MetricDimensions
  metricCount?: number
}
export type MetricsTraceOptions = {
  traceName?: string
  traceAnnotations?: TraceAnnotations
  metricName: string
  metricDimensions?: MetricDimensions
  useTracing?: boolean
}

export type Metrics = {
  count: (options: MetricsCountOptions) => void
  traceAsync: <TResult>(
    options: MetricsTraceOptions,
    fn: () => Promise<TResult>,
  ) => Promise<TResult>
  settlePromises: () => Promise<('rejected' | 'fulfilled')[]>
  traceId: () => { header: string; value: string | undefined } | null
}

export const createMetrics = (params?: { useTracing: boolean }): Metrics => {
  const pendingPromises: Promise<void>[] = []

  const tracer = getTracer(params)
  const metricsLogger = getMetricsLogger()
  const loggingIsEnabled =
    isTracingModeActive() && process.env.MNPG_ENVIRONMENT_NAME === 'local'

  const settlePromises = async () => {
    // This is called from Node.js only so Promise.allSettled is safe to use
    // eslint-disable-next-line compat/compat
    const results = await Promise.allSettled(Array.from(pendingPromises))
    return results.map(result => result.status)
  }

  const count = ({
    metricName,
    metricDimensions = {},
    metricCount: n = 1,
  }: {
    metricName: string
    metricDimensions?: { [id: string]: string }
    metricCount?: number
  }) => {
    if (metricsLogger) {
      const promise = metricsLogger
        .withDimensionSets(metricDimensions)
        .count(`${metricName}-count`, n)
      pendingPromises.push(promise)
    }

    if (loggingIsEnabled) {
      logger.info(`count ${metricName} ${JSON.stringify(metricDimensions)}`)
    }
  }

  const traceAsync = <TResult>(
    {
      traceName,
      traceAnnotations,
      metricName,
      metricDimensions = {},
    }: {
      traceName?: string
      traceAnnotations?: TraceAnnotations
      metricName: string
      metricDimensions?: MetricDimensions
    },
    fn: () => Promise<TResult>,
  ): Promise<TResult> => {
    const spanName = traceName ?? metricName

    if (isServer && tracer && traceName) {
      return tracer.startActiveSpan(spanName, async span => {
        const start = Date.now()
        try {
          if (traceAnnotations) {
            span.setAttributes(traceAnnotations)
          }

          const result = await fn()
          const took = Date.now() - start

          if (metricsLogger) {
            const promise = metricsLogger
              .withDimensionSets({
                ...metricDimensions,
                error: 'false',
              })
              .durationSince(`${metricName}-duration`, start)
            pendingPromises.push(promise)
          }

          if (loggingIsEnabled) {
            logger.info(`trace ${spanName} took ${took}ms`)
          }

          span.setStatus({ code: SpanStatusCode.OK })
          span.end()
          return result
        } catch (error) {
          const err = toError(error)
          const took = Date.now() - start

          if (metricsLogger) {
            const promise = metricsLogger
              .withDimensionSets({
                ...metricDimensions,
                error: 'true',
              })
              .durationSince(`${metricName}-duration`, start)
            pendingPromises.push(promise)
          }

          if (loggingIsEnabled) {
            logger.info(`trace ${spanName} took ${took}ms, error "${err}"`)
          }

          span.recordException(err)
          span.setStatus({ code: SpanStatusCode.ERROR, message: err.message })
          span.end()
          throw error
        }
      })
    }

    return (async () => {
      const start = Date.now()
      try {
        const result = await fn()
        const took = Date.now() - start

        if (metricsLogger) {
          const promise = metricsLogger
            .withDimensionSets({
              ...metricDimensions,
              error: 'false',
            })
            .durationSince(`${metricName}-duration`, start)
          pendingPromises.push(promise)
        }

        if (loggingIsEnabled) {
          logger.info(`trace ${spanName} took ${took}ms`)
        }

        return result
      } catch (error) {
        const err = toError(error)
        const took = Date.now() - start

        if (metricsLogger) {
          const promise = metricsLogger
            .withDimensionSets({
              ...metricDimensions,
              error: 'true',
            })
            .durationSince(`${metricName}-duration`, start)
          pendingPromises.push(promise)
        }

        if (loggingIsEnabled) {
          logger.info(`trace ${spanName} took ${took}ms, error "${err}"`)
        }

        throw error
      }
    })()
  }

  return {
    count,
    traceAsync,
    settlePromises,
    traceId: () => {
      if (isTracingModeActive()) {
        return {
          header: 'X-Amzn-Trace-Id',
          value: process.env._X_AMZN_TRACE_ID,
        }
      }
      return null
    },
  }
}

const getMetricsLogger = (): LambdaMetrics | undefined => {
  const metricsIsEnabled =
    process.env.ENABLE_CLOUDWATCH_METRICS === 'true' &&
    LambdaMetrics &&
    Boolean(process.env.LAMBDA_TASK_ROOT)
  return metricsIsEnabled
    ? new LambdaMetrics({
        metricsNamespace:
          process.env.APP_NAME || /* istanbul ignore next */ 'web',
        metricsDimensions: {
          'function-name':
            process.env.AWS_LAMBDA_FUNCTION_NAME ||
            /* istanbul ignore next */ 'unknown',
        },
      })
    : undefined
}

const isTracingModeActive = () => (process.env.TRACING_MODE || 'off') !== 'off'

const getTracer = (params: { useTracing: boolean } | undefined) => {
  const tracingIsEnabled = params?.useTracing ?? true
  return tracingIsEnabled && isTracingModeActive()
    ? trace.getTracer('core/monitoring')
    : undefined
}

const isServer = typeof window === 'undefined'

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error
  }
  /* istanbul ignore next */
  return new Error(`Unknown error: ${error}`)
}
